import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AdvertiseThankYou } from '@/components/advertise/AdvertiseThankYou';
import { getBoostOrder } from '@/services/advertiseApi';
import { useAdvertise } from '@/store/useAdvertise';
import { formatPrice } from '@/utils/format';
import { ADVERTISE_SELLERS } from '@/constants/testIds/advertise';

export default function SellersThankYou() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const reset = useAdvertise((s) => s.reset);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);
  const [missingSession, setMissingSession] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(false);
    setMissingSession(false);
    if (!sessionId) {
      reset('sellers');
      setMissingSession(true);
      return;
    }
    setOrder(null);
    getBoostOrder(sessionId)
      .then((o) => {
        if (!alive) return;
        setOrder(o);
        reset('sellers');
      })
      .catch(() => {
        if (!alive) return;
        setError(true);
      });
    return () => { alive = false; };
  }, [sessionId, retryKey, reset]);

  if (missingSession) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="font-display text-2xl font-bold text-slate-900">Payment session missing</h1>
        <p className="max-w-md text-slate-600">
          We could not confirm your boost payment because this page was opened without a checkout session.
          If you just paid, return from your payment confirmation link or contact support.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Button asChild className="rounded-xl">
            <Link to="/advertise/sellers">Start again</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl">
            <Link to="/contact">Contact support</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-slate-700">We could not confirm your payment status.</p>
        <Button className="rounded-xl" onClick={() => setRetryKey((k) => k + 1)}>Try again</Button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <Loader2 size={28} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <AdvertiseThankYou
      testId={ADVERTISE_SELLERS.thankYouHeading}
      heading="Your listing is boosted!"
      subtitle={order?.amount ? `Payment of ${formatPrice(order.amount)} confirmed — your boost is live and stays active until your home sells.` : 'Your boost is live and stays active until your home sells.'}
      secondaryLinks={[
        { to: '/', label: 'Back to casahom', variant: 'outline' },
        { to: '/dashboard/my-listings', label: 'View my listings' },
      ]}
    />
  );
}
